import { ActionButton } from '@/components/common/ActionButton';
import { MOTION } from '@/components/common/motion';
import { PAGE_TITLE_CLASSES } from '@/components/common/PageHeader';
import type { UpdateProjectDto } from '@/types/project.types';
import { Check, PencilSimple, X } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProjectTitleEditorProps {
  title: string;
  onSave: (updates: UpdateProjectDto) => Promise<void>;
}

/**
 * The input the title turns into. Sized like the `h1` it replaces, so the line does not jump.
 *
 * It carries no gradient: `bg-clip-text`/`text-transparent` would make the text being typed
 * invisible, which is the one thing an editable title cannot afford.
 */
const TITLE_INPUT_CLASSES = `w-full rounded-lg border border-gray-300 px-3 py-1 text-3xl font-semibold tracking-tight text-gray-900 ${MOTION} focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;

/**
 * The project title, renamable in place, rendered into `PageHeader`'s title slot.
 *
 * Both modes render the page's one `h1` — reading mode as the shared gradient title, edit mode as
 * an `h1` wrapping the input. The heading never disappears mid-edit, so the document outline
 * stays intact while someone is typing.
 */
export const ProjectTitleEditor: FC<ProjectTitleEditorProps> = ({ title, onSave }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus on entering edit mode rather than through `autoFocus`, which would also steal focus on
  // any remount of an already-open editor.
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editedTitle.trim() === '') {
      return;
    }

    if (editedTitle === title) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave({ title: editedTitle });
      setIsEditing(false);
    } catch (_error) {
      // Error is handled by the hook
      setEditedTitle(title); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Fire-and-forget entry point for the two callers that cannot await.
   *
   * `handleSave` already owns its failure path — it reverts the field — so there is nothing left
   * for an event handler to do with the promise.
   */
  const requestSave = () => {
    handleSave();
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <h1 className="m-0 min-w-0 flex-1 basis-64 p-0 font-normal">
          <input
            ref={inputRef}
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            className={TITLE_INPUT_CLASSES}
            disabled={isSaving}
            aria-label={t('projectDetail.editTitle')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                requestSave();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
          />
        </h1>
        <div className="flex shrink-0 items-center gap-2">
          <ActionButton
            variant="primary"
            iconOnly={true}
            size="md"
            icon={<Check size={16} aria-hidden="true" />}
            onClick={requestSave}
            loading={isSaving}
            disabled={editedTitle.trim() === ''}
            aria-label={t('common.save')}
            title={t('common.save')}
          />
          <ActionButton
            variant="ghost"
            iconOnly={true}
            size="md"
            icon={<X size={16} aria-hidden="true" />}
            onClick={handleCancel}
            disabled={isSaving}
            aria-label={t('common.cancel')}
            title={t('common.cancel')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <h1 className={PAGE_TITLE_CLASSES}>{title}</h1>
      <ActionButton
        variant="ghost"
        iconOnly={true}
        icon={<PencilSimple size={16} aria-hidden="true" />}
        onClick={() => setIsEditing(true)}
        aria-label={t('projectDetail.editTitle')}
        title={t('projectDetail.editTitle')}
      />
    </div>
  );
};
